import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import { decideContactsToApprove, getContactsToApprove } from 'API/linkedin-funnel';
import { ContactToApprove } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  /** Kampagne, deren wartende Kontakte gezeigt werden. */
  funnelId: string | null;

  handleClose(): void;

  /** Nach jeder Entscheidung: wie viele Kontakte noch warten. */
  handleRemainingChange(funnelId: string, remaining: number): void;

  handleOpenContactDrawer(url: string, profileId: string, funnelId?: string): () => void;
}

/**
 * Kontakte freigeben — wie „Nachrichten freigeben", nur je Kontakt: Mit der Kampagneneinstellung
 * `approveContacts` beginnt die Ansprache erst, wenn der Kontakt hier freigegeben ist. Ablehnen beendet ihn
 * in dieser Kampagne.
 */
const ApproveContactsDialog: React.FC<Props> = ({ open, funnelId, handleClose, handleRemainingChange, handleOpenContactDrawer }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const [contacts, setContacts] = useState<ContactToApprove[]>([]);
  const [total, setTotal] = useState(0);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !funnelId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setIndex(0);
    getContactsToApprove(funnelId).then((res) => {
      if (cancelled) return;
      setContacts(res.contacts ?? []);
      setTotal(res.total ?? 0);
      setError(!_.isNil(res.error));
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [open, funnelId]);

  const contact = contacts[index];

  const decide = async (ids: string[] | 'all', approve: boolean) => {
    if (!funnelId) return;
    setLoading(true);
    const res = await decideContactsToApprove(funnelId, ids, approve);
    if (!_.isNil(res.error)) {
      setError(true);
      setLoading(false);
      return;
    }
    handleRemainingChange(funnelId, res.remaining);
    if (ids === 'all' || res.remaining === 0) {
      setLoading(false);
      handleClose();
      return;
    }
    const rest = contacts.filter((c) => !ids.includes(c.campaignContactId));
    // Die geladene Seite ist abgearbeitet, aber es warten noch weitere: nachladen.
    if (rest.length === 0) {
      const next = await getContactsToApprove(funnelId);
      setContacts(next.contacts ?? []);
      setTotal(next.total ?? 0);
      setIndex(0);
    } else {
      setContacts(rest);
      setTotal(res.remaining);
      setIndex((i) => Math.min(i, rest.length - 1));
    }
    setLoading(false);
  };

  const handleNext = () => setIndex((i) => (contacts.length ? (i + 1) % contacts.length : 0));
  const handlePrev = () => setIndex((i) => (contacts.length ? (i - 1 + contacts.length) % contacts.length : 0));

  const subtitle = [contact?.occupation || contact?.headline, contact?.company].filter(Boolean).join(' · ');

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'md'} onClose={handleClose}>
      <DialogTitle className={classes.title}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography className={classes.titleText}>{t('contactsToApprove')}</Typography>
          <IconButton className={classes.closeBtn} size={'small'} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent className={classes.stepper}>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <IconButton className={classes.iconButton} onClick={handlePrev} disabled={loading || contacts.length < 2}>
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography className={classes.greyText}>{`${t('contact')} ${contacts.length ? index + 1 : 0} / ${total}`}</Typography>
          <IconButton className={classes.iconButton} onClick={handleNext} disabled={loading || contacts.length < 2}>
            <ArrowForwardRoundedIcon />
          </IconButton>
        </Stack>
      </DialogContent>

      <DialogContent className={classes.content}>
        {loading ? (
          <PaiDotLoader />
        ) : error ? (
          <Typography className={classes.greyText}>{t('contactsToApproveError')}</Typography>
        ) : !contact ? (
          <Typography className={classes.greyText}>{t('contactsToApproveEmpty')}</Typography>
        ) : (
          <Stack gap={2}>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Avatar src={contact.profilePictureUrl} className={classes.avatar}>
                {contact.name.charAt(0)}
              </Avatar>
              <Stack gap={0.5} minWidth={0}>
                {contact.linkedInURL ? (
                  <PaiLink
                    text={contact.name}
                    size={'lg'}
                    variant={'default'}
                    underline={'always'}
                    action={handleOpenContactDrawer(contact.linkedInURL, contact.profileId, funnelId ?? undefined)}
                  />
                ) : (
                  <Typography className={classes.name}>{contact.name}</Typography>
                )}
                {subtitle ? <Typography className={classes.text}>{subtitle}</Typography> : null}
                {contact.location ? <Typography className={classes.greyText}>{contact.location}</Typography> : null}
              </Stack>
            </Stack>

            {contact.headline && contact.headline !== contact.occupation ? (
              <Typography className={classes.text}>{contact.headline}</Typography>
            ) : null}

            {contact.judgingGrade ? (
              <Stack gap={1}>
                <Stack direction={'row'} alignItems={'center'} gap={1}>
                  <Chip
                    size={'small'}
                    label={_.isNil(contact.judgingScore) ? contact.judgingGrade : `${contact.judgingGrade} · ${contact.judgingScore}`}
                    className={classes.gradeChip}
                  />
                  {contact.searchName ? <Typography className={classes.greyText}>{contact.searchName}</Typography> : null}
                </Stack>
                {contact.judgingExplanation ? <Typography className={classes.text}>{contact.judgingExplanation}</Typography> : null}
              </Stack>
            ) : null}

            {contact.linkedInURL ? (
              <PaiLink text={t('openLinkedInProfile')} size={'md'} variant={'primary'} underline={'hover'} href={contact.linkedInURL} target={'_blank'} />
            ) : null}
          </Stack>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <PaiLink
          text={t('approveAllContacts', { count: total })}
          size={'lg'}
          variant={'default'}
          underline={'always'}
          disabled={loading || !contact}
          action={() => decide('all', true)}
        />
        <PaiLink
          text={t('declineContact')}
          size={'lg'}
          variant={'default'}
          underline={'always'}
          disabled={loading || !contact}
          action={() => contact && decide([contact.campaignContactId], false)}
        />
        <PaiButton
          text={t('approveContact')}
          size={'small'}
          border={'rounded'}
          disabled={!contact}
          loading={loading}
          action={() => contact && decide([contact.campaignContactId], true)}
        />
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: '16px',
  },
  title: {
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  closeBtn: {
    color: '#FFF',
    '& svg': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  stepper: {
    padding: '16px 32px !important',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  content: {
    padding: '20px 24px !important',
    minHeight: '180px',
  },
  avatar: {
    width: 56,
    height: 56,
  },
  name: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 600,
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
    whiteSpace: 'pre-wrap',
  },
  greyText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.greys.medium,
  },
  gradeChip: {
    fontWeight: 600,
  },
  iconButton: {
    padding: '2px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
  actions: {
    borderTop: `1px solid ${theme.palette.greys.light}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '20px 24px',
  },
}));

export default ApproveContactsDialog;
